
import React, { useState } from 'react';
import { GraduationCap, Stethoscope, PaintBucket, Factory, Code, Rocket, Building2, ArrowRight } from 'lucide-react';

type Industry = {
  id: string;
  name: string;
  icon: React.ReactNode;
  description: string;
  caseStudy: {
    title: string;
    client: string;
    challenge: string;
    solution: string;
    result: string;
    image: string;
  };
};

const IndustriesSection = () => {
  const industries: Industry[] = [
    {
      id: 'education',
      name: 'Education',
      icon: <GraduationCap className="h-10 w-10" />,
      description: 'Custom hardware solutions for schools, colleges and educational institutions.',
      caseStudy: {
        title: 'Empowering Digital Learning at Scale',
        client: 'National Educational Network',
        challenge: 'Deploy 5,000 reliable, affordable computers across 200 schools while ensuring consistent performance and ease of management.',
        solution: 'Custom-designed educational desktop systems with remote management capabilities and enhanced durability for classroom environments.',
        result: 'Reduced IT support calls by 65%, extended hardware lifespan by 40% compared to previous solutions, and increased student technology access by 300%.',
        image: 'https://images.unsplash.com/photo-1486312338219-ce68d2c6f44d?w=600&auto=format&fit=crop'
      }
    },
    {
      id: 'healthcare',
      name: 'Healthcare',
      icon: <Stethoscope className="h-10 w-10" />,
      description: 'Specialized medical-grade devices and workstations for healthcare providers.',
      caseStudy: {
        title: 'Enhancing Patient Care with Purpose-Built Technology',
        client: 'Regional Hospital Network',
        challenge: 'Create medical-grade tablets for bedside care that integrate with existing hospital systems while meeting strict sanitization requirements.',
        solution: 'Custom antimicrobial tablets with specialized casing for medical environments, optimized battery life, and secure healthcare software integration.',
        result: 'Reduced charting time by 45%, improved data accuracy by 30%, and achieved 99.8% device reliability in critical care settings.',
        image: 'https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d?w=600&auto=format&fit=crop'
      }
    },
    {
      id: 'creative',
      name: 'Creative Agencies',
      icon: <PaintBucket className="h-10 w-10" />,
      description: 'High-performance workstations for design, video, and creative professionals.',
      caseStudy: {
        title: 'Accelerating Creative Workflows',
        client: 'VisualStorm Productions',
        challenge: 'Build rendering workstations powerful enough to handle 8K video editing and complex 3D animations without performance bottlenecks.',
        solution: 'Custom-engineered workstations with top-tier GPUs, optimized cooling solutions, and specialized calibration for color-critical work.',
        result: 'Rendering times reduced by 60%, seamless multi-application workflows, and elimination of thermal throttling during intensive projects.',
        image: 'https://images.unsplash.com/photo-1585184394271-4c0a47dc59c9?w=600&auto=format&fit=crop'
      }
    },
    {
      id: 'manufacturing',
      name: 'Industrial Manufacturing',
      icon: <Factory className="h-10 w-10" />,
      description: 'Rugged, reliable hardware for factory floors and industrial environments.',
      caseStudy: {
        title: 'Revolutionizing Factory Floor Operations',
        client: 'PrecisionForge Industries',
        challenge: 'Develop computing systems for a manufacturing environment with extreme temperatures, dust, and vibration that can reliably run 24/7.',
        solution: 'Fully ruggedized industrial computers with specialized thermal management, filtration systems, and shock absorption technology.',
        result: 'Achieved 99.9% uptime in harsh conditions, eliminated environmentally-caused failures, and extended equipment lifecycle by 3+ years.',
        image: 'https://images.unsplash.com/photo-1581091226033-d5c48150dbaa?w=600&auto=format&fit=crop'
      }
    },
    {
      id: 'software',
      name: 'Software Companies',
      icon: <Code className="h-10 w-10" />,
      description: 'Optimized development environments and testing infrastructure for software teams.',
      caseStudy: {
        title: 'Scaling Development Infrastructure',
        client: 'CloudSoft Technologies',
        challenge: 'Create developer workstations that would minimize compile times and support complex testing environments for a team of 200+ developers.',
        solution: 'Customized development rigs with top-tier CPUs, extensive RAM configurations, and optimized storage arrays for development workflows.',
        result: 'Build times reduced by 70%, developer productivity increased by 40%, and consistent environment performance across distributed teams.',
        image: 'https://images.unsplash.com/photo-1498050108023-c5249f4df085?w=600&auto=format&fit=crop'
      }
    },
    {
      id: 'startups',
      name: 'Startups',
      icon: <Rocket className="h-10 w-10" />,
      description: 'Scalable, cost-effective technology solutions for growing businesses.',
      caseStudy: {
        title: 'Technology Foundation for Rapid Growth',
        client: 'InnoVenture AI',
        challenge: 'Equip a fast-growing startup with flexible technology infrastructure that can scale from 10 to 100 employees within 18 months.',
        solution: 'Modular workstation designs with standardized components and easy upgrade paths, plus centralized device management.',
        result: 'Successfully scaled hardware deployment with 40% cost savings compared to off-the-shelf solutions and zero productivity disruption during growth.',
        image: 'https://images.unsplash.com/photo-1559136555-9303baea8ebd?w=600&auto=format&fit=crop'
      }
    },
    {
      id: 'government',
      name: 'Government & Enterprise',
      icon: <Building2 className="h-10 w-10" />,
      description: 'Secure, compliant hardware solutions for governmental and large organizations.',
      caseStudy: {
        title: 'Ensuring Security and Compliance at Scale',
        client: 'Municipal Government Authority',
        challenge: 'Implement secure computing systems for 1,200 government employees that meet stringent data protection regulations and compliance requirements.',
        solution: 'Custom-built secure workstations with hardware-level encryption, specialized security features, and comprehensive device management.',
        result: 'Achieved 100% compliance with security regulations, reduced security incidents by 85%, and streamlined IT management across multiple departments.',
        image: 'https://images.unsplash.com/photo-1523509433743-6f42a58221df?w=600&auto=format&fit=crop'
      }
    }
  ];
  
  const [selectedIndustry, setSelectedIndustry] = useState<string>('education');
  
  // Get the currently selected industry
  const currentIndustry = industries.find(i => i.id === selectedIndustry) || industries[0];
  
  return (
    <section id="industries" className="section-padding">
      <div className="container mx-auto container-padding">
        <div className="text-center mb-12">
          <h2 className="text-3xl lg:text-4xl font-bold gradient-text mb-4">
            Industries We Serve
          </h2>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
            Specialized technology solutions designed for the unique challenges of various industries.
            Discover how we've helped organizations like yours.
          </p>
        </div>
        
        {/* Industry Selection */}
        <div className="flex flex-wrap justify-center gap-4 mb-12">
          {industries.map(industry => (
            <button
              key={industry.id}
              onClick={() => setSelectedIndustry(industry.id)}
              className={`flex flex-col items-center p-4 rounded-lg transition-all duration-300 min-w-[120px] ${
                selectedIndustry === industry.id 
                  ? 'bg-primary text-white shadow-md scale-105' 
                  : 'bg-white hover:bg-muted/50 text-foreground border border-border/50'
              }`}
            >
              <div className={`${
                selectedIndustry === industry.id ? 'text-white' : 'text-primary'
              }`}>
                {industry.icon}
              </div>
              <span className="mt-2 text-sm font-medium">{industry.name}</span>
            </button>
          ))}
        </div>
        
        {/* Case Study */}
        <div className="bg-white rounded-xl overflow-hidden shadow-md border border-border/50">
          <div className="grid grid-cols-1 lg:grid-cols-2">
            {/* Case Study Image */}
            <div className="relative h-full min-h-[400px] overflow-hidden">
              <img 
                src={currentIndustry.caseStudy.image} 
                alt={currentIndustry.caseStudy.title} 
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-tech-dark/80 to-transparent">
                <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                  <div className="inline-block bg-primary/90 px-3 py-1 rounded-full text-sm font-medium mb-3">
                    {currentIndustry.name} Case Study
                  </div>
                  <h3 className="text-2xl font-bold mb-2">{currentIndustry.caseStudy.title}</h3>
                  <p className="text-gray-200">Client: {currentIndustry.caseStudy.client}</p>
                </div>
              </div>
            </div>
            
            {/* Case Study Info */}
            <div className="p-6 lg:p-8">
              <div className="space-y-6">
                <div>
                  <h4 className="text-lg font-semibold mb-2 text-primary">The Challenge</h4>
                  <p>{currentIndustry.caseStudy.challenge}</p>
                </div>
                
                <div>
                  <h4 className="text-lg font-semibold mb-2 text-secondary">Our Solution</h4>
                  <p>{currentIndustry.caseStudy.solution}</p>
                </div>
                
                <div>
                  <h4 className="text-lg font-semibold mb-2 text-accent">The Results</h4>
                  <p>{currentIndustry.caseStudy.result}</p>
                </div>
              </div>
              
              <div className="mt-8">
                <a 
                  href="#contact" 
                  className="tech-btn-outline flex items-center justify-center"
                >
                  Discuss Your Industry Needs
                  <ArrowRight className="ml-2 h-5 w-5" />
                </a>
              </div>
            </div>
          </div>
        </div>
        
        {/* Industry Description */}
        <div className="mt-16 text-center">
          <h3 className="text-2xl font-bold mb-4">Custom Solutions for Every Industry</h3>
          <p className="text-muted-foreground max-w-2xl mx-auto mb-8">
            No matter your sector, we understand the unique technical challenges you face.
            Our team works closely with your organization to develop hardware solutions
            that address your specific industry requirements.
          </p>
        </div>
      </div>
    </section>
  );
};

export default IndustriesSection;
